import type { ChartConfig } from "../../api/ai";

interface DynamicChartPanelProps {
  columns: string[];
  rows: Record<string, unknown>[];
  chart?: ChartConfig;
}

export function DynamicChartPanel({ columns, rows, chart }: DynamicChartPanelProps) {
  if (!rows.length) {
    return <p className="text-xs text-slate-500">لا توجد صفوف.</p>;
  }

  if (!chart || chart.type === "table") {
    return (
      <div className="max-h-64 overflow-auto rounded border border-slate-200">
        <table className="w-full text-left text-[11px]" dir="ltr">
          <thead className="sticky top-0 bg-slate-100">
            <tr>
              {columns.map((c) => (
                <th key={c} className="px-2 py-1 font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((row, i) => (
              <tr key={i} className="border-t border-slate-100">
                {columns.map((c) => (
                  <td key={c} className="px-2 py-1 font-mono">
                    {String(row[c] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const xKey = chart.xKey ?? columns[0];
  const yKey = chart.yKey ?? columns[1];
  const maxVal = Math.max(
    ...rows.map((r) => Number(r[yKey ?? ""] ?? 0)).filter((n) => Number.isFinite(n)),
    1,
  );

  return (
    <div className="space-y-2">
      {chart.title && <p className="text-xs font-semibold text-slate-700">{chart.title}</p>}
      <ul className="space-y-1">
        {rows.slice(0, 20).map((row, i) => {
          const label = String(row[xKey ?? ""] ?? i);
          const val = Number(row[yKey ?? ""] ?? 0);
          const pct = Number.isFinite(val) ? Math.round((val / maxVal) * 100) : 0;
          return (
            <li key={i} className="flex items-center gap-2 text-[11px]">
              <span className="w-24 truncate text-slate-600" title={label}>
                {label}
              </span>
              <div className="h-2 flex-1 rounded bg-slate-100">
                <div
                  className="h-2 rounded bg-godo-primary"
                  style={{ width: `${Math.max(pct, 4)}%` }}
                />
              </div>
              <span className="w-16 text-right font-mono">{val}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
